import { Component } from '@theme/component';

export class TdPriceRangeFacetComponent extends Component {
  static observedAttributes = /** @type {string[]} */ (['data-current-min', 'data-current-max', 'data-range-max']);

  constructor() {
    super();

    this.minParam = this.dataset.minParam || '';
    this.maxParam = this.dataset.maxParam || '';
    // Round values to prevent floating-point display glitches
    this.rangeMax = Math.round(parseFloat(this.dataset.rangeMax || '0'));
    this.currentMin = Math.round(parseFloat(this.dataset.currentMin || '0')) || 0;
    this.currentMax = Math.round(parseFloat(this.dataset.currentMax || '0')) || this.rangeMax;
    // Used as the minimum gap between min/max slider handles (0 allows any range width).
    const parsedIncrement = parseFloat(this.dataset.increment ?? '0');
    this.increment = Number.isFinite(parsedIncrement) ? parsedIncrement : 0;
    this.syncTimeout = null;
    this.pendingMinValue = null;
    this.pendingMaxValue = null;

    /** @type {(e: Event) => void} */
    this.onRangeInputChange = (e) => this.handleCheckboxChange(e);
    /** @type {(e: MouseEvent) => void} */
    this.onRangeInputClick = (e) => this.handleRangeInputClick(e);
  }

  /**
   * Find the facets form element where filter params must live.
   * We can't always rely on closest('form') depending on layout/portals.
   * @returns {HTMLFormElement | null}
   */
  #getFacetsForm() {
    const direct = this.closest('form');
    if (direct instanceof HTMLFormElement) return direct;

    const facetsFormComponent = this.closest('facets-form-component');
    if (facetsFormComponent instanceof HTMLElement) {
      const form = facetsFormComponent.querySelector('form[ref="facetsForm"]');
      if (form instanceof HTMLFormElement) return form;
    }

    return null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up MutationObservers to prevent memory leaks
    if (this.trackObserver) {
      this.trackObserver.disconnect();
      this.trackObserver = null;
    }

    if (this.checkboxObserver) {
      this.checkboxObserver.disconnect();
      this.checkboxObserver = null;
    }

    // Clear any pending timeouts
    if (this.syncTimeout) {
      clearTimeout(this.syncTimeout);
      this.syncTimeout = null;
    }
  }

  attributeChangedCallback(/** @type {string} */ _name, /** @type {string | null} */ oldValue, /** @type {string | null} */ newValue) {
    if (oldValue !== newValue) {
      // Debounce to avoid multiple rapid syncs
      if (this.syncTimeout) {
        clearTimeout(this.syncTimeout);
      }

      this.syncTimeout = setTimeout(() => {
        this.syncValuesFromDataset();
      }, 0);
    }
  }

  updatedCallback() {
    super.updatedCallback();

    // Always sync and update track when component updates
    // This ensures track is visible even when other filters change
    requestAnimationFrame(() => {
      // If we have pending values from a slider drag, restore them immediately
      if (this.pendingMinValue !== null || this.pendingMaxValue !== null) {
        const minInput = this.refs.minInput;
        const maxInput = this.refs.maxInput;
        
        if (this.pendingMinValue !== null && minInput && !Array.isArray(minInput)) {
          /** @type {HTMLInputElement} */ (minInput).value = String(this.pendingMinValue);
        }
        if (this.pendingMaxValue !== null && maxInput && !Array.isArray(maxInput)) {
          /** @type {HTMLInputElement} */ (maxInput).value = String(this.pendingMaxValue);
        }
        
        // Clear pending values
        this.pendingMinValue = null;
        this.pendingMaxValue = null;
      } else {
        this.syncValuesFromDataset();
      }

      if (this.trackObserver) {
        this.trackObserver.disconnect();
      }
      this.setupTrackObserver();
    });
  }

  syncValuesFromDataset() {
    // Round values to prevent floating-point display glitches (e.g., from money_without_currency filter)
    this.rangeMax = Math.round(parseFloat(this.dataset.rangeMax || '0'));
    this.currentMin = Math.round(parseFloat(this.dataset.currentMin || '0')) || 0;
    this.currentMax = Math.round(parseFloat(this.dataset.currentMax || '0')) || this.rangeMax;

    const sliderMin = this.refs.sliderMin;
    const sliderMax = this.refs.sliderMax;
    const minInput = this.refs.minInput;
    const maxInput = this.refs.maxInput;

    if (sliderMin && !Array.isArray(sliderMin)) {
      /** @type {HTMLInputElement} */ (sliderMin).value = String(this.currentMin);
    }
    if (sliderMax && !Array.isArray(sliderMax)) {
      /** @type {HTMLInputElement} */ (sliderMax).value = String(this.currentMax);
    }
    if (minInput && !Array.isArray(minInput)) {
      /** @type {HTMLInputElement} */ (minInput).value = this.currentMin > 0 ? String(this.currentMin) : '';
    }
    if (maxInput && !Array.isArray(maxInput)) {
      const maxVal = this.currentMax < this.rangeMax ? String(this.currentMax) : '';
      /** @type {HTMLInputElement} */ (maxInput).value = maxVal;
    }

    this.updateSliderTrack();
  }

  init() {
    // Re-read params from dataset to ensure we have current values
    // (constructor may run before attributes are available)
    this.minParam = this.dataset.minParam || this.minParam || '';
    this.maxParam = this.dataset.maxParam || this.maxParam || '';

    // Setup checkbox listeners for desktop
    const checkboxes = this.refs.rangeCheckbox;

    if (checkboxes && Array.isArray(checkboxes)) {
      checkboxes.forEach((checkbox) => {
        const input = /** @type {HTMLInputElement} */ (checkbox);
        checkbox.removeEventListener('change', this.onRangeInputChange);
        checkbox.addEventListener('change', this.onRangeInputChange);
        checkbox.removeEventListener('click', this.onRangeInputClick);
        checkbox.addEventListener('click', this.onRangeInputClick);

        // Force sync the .checked property with the checked attribute on init
        // This is needed because the property doesn't always sync with attribute on initial load
        if (input.hasAttribute('checked')) {
          input.checked = true;
        }
      });
    }

    // Setup checkbox observer to watch for DOM changes
    this.setupCheckboxObserver();

    const sliderMin = this.refs.sliderMin;
    const sliderMax = this.refs.sliderMax;
    if (sliderMin && !Array.isArray(sliderMin) && sliderMax && !Array.isArray(sliderMax)) {
      // 'input' event fires continuously while dragging (for visual updates only)
      sliderMin.addEventListener('input', () => this.handleSliderChange(false));
      sliderMax.addEventListener('input', () => this.handleSliderChange(false));
      
      // 'change' event fires when user releases the slider (trigger filter update)
      sliderMin.addEventListener('change', () => this.handleSliderChange(true));
      sliderMax.addEventListener('change', () => this.handleSliderChange(true));

      requestAnimationFrame(() => {
        this.updateSliderTrack();
      });
    }

    const minInput = this.refs.minInput;
    const maxInput = this.refs.maxInput;
    if (minInput && !Array.isArray(minInput) && maxInput && !Array.isArray(maxInput)) {
      minInput.addEventListener('input', () => this.handleInputChange());
      maxInput.addEventListener('input', () => this.handleInputChange());

      // Clear input value on focus (for both placeholder and actual values)
      minInput.addEventListener('click', (e) => {
        const input = /** @type {HTMLInputElement} */ (e.target);
        if (input.value) {
          input.value = '';
        }
      });
      maxInput.addEventListener('click', (e) => {
        const input = /** @type {HTMLInputElement} */ (e.target);
        if (input.value) {
          input.value = '';
        }
      });
    }

    this.setupTrackObserver();
  }

  setupTrackObserver() {
    const sliderTrack = this.refs.sliderTrack;
    if (!sliderTrack || Array.isArray(sliderTrack)) return;

    // Watch for changes to the track element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const track = /** @type {HTMLElement} */ (mutation.target);
          // If track lost its styles, re-apply them
          if (!track.style.left || !track.style.right) {
            this.updateSliderTrack();
          }
        }
      });
    });

    observer.observe(sliderTrack, {
      attributes: true,
      attributeFilter: ['style']
    });

    this.trackObserver = observer;
  }

  setupCheckboxObserver() {
    const checkboxContainer = this.querySelector('.price-range-facet__checkboxes, .price-range-facet__list');
    if (!checkboxContainer) return;

    // Watch for DOM and attribute changes to sync .checked property with checked attribute
    // This fixes the issue where CSS :checked doesn't apply after AJAX filter updates
    const observer = new MutationObserver(() => {
      const checkboxes = this.refs.rangeCheckbox;
      if (checkboxes && Array.isArray(checkboxes)) {
        checkboxes.forEach((checkbox) => {
          const input = /** @type {HTMLInputElement} */ (checkbox);

          // Sync .checked property with checked attribute
          if (input.hasAttribute('checked')) {
            input.checked = true;
          } else {
            input.checked = false;
          }

          // Re-attach event listener after DOM replacement
          checkbox.removeEventListener('change', this.onRangeInputChange);
          checkbox.addEventListener('change', this.onRangeInputChange);
          checkbox.removeEventListener('click', this.onRangeInputClick);
          checkbox.addEventListener('click', this.onRangeInputClick);
        });
      }
    });

    observer.observe(checkboxContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['checked']
    });

    this.checkboxObserver = observer;
  }

  /**
   * Allows clicking the currently-checked radio again to clear the price filter.
   * We key off the presence of the `checked` ATTRIBUTE (which we keep in sync)
   * instead of the `.checked` PROPERTY, because different browsers can update
   * `.checked` at different points during the click event.
   *
   * @param {MouseEvent} e
   */
  handleRangeInputClick(e) {
    const target = /** @type {HTMLInputElement} */ (e.target);
    if (!target) return;
    if (target.type !== 'radio') return;

    // Only toggle-off when clicking the already-selected preset
    // (the one currently represented by the `checked` attribute).
    if (!target.hasAttribute('checked')) return;

    // Toggle off: clear UI state + remove hidden inputs + update filters.
    e.preventDefault();
    e.stopPropagation();

    // Uncheck preset radios.
    // Some browsers can re-assert a radio's checked state after the click event,
    // so we clear immediately AND on the next frame.
    const uncheckAllPresetRadios = () => {
      const checkboxes = this.refs.rangeCheckbox;
      if (checkboxes && Array.isArray(checkboxes)) {
        checkboxes.forEach((checkbox) => {
          const input = /** @type {HTMLInputElement} */ (checkbox);
          input.checked = false;
          input.removeAttribute('checked');
        });
      } else {
        target.checked = false;
        target.removeAttribute('checked');
      }
    };

    uncheckAllPresetRadios();
    requestAnimationFrame(() => uncheckAllPresetRadios());

    // Clear the mobile input values (they have the same name as the filter params)
    const minInput = this.refs.minInput;
    const maxInput = this.refs.maxInput;
    if (minInput && !Array.isArray(minInput)) {
      /** @type {HTMLInputElement} */ (minInput).value = '';
    }
    if (maxInput && !Array.isArray(maxInput)) {
      /** @type {HTMLInputElement} */ (maxInput).value = '';
    }

    // Remove hidden price filter inputs
    this.#removeHiddenInputs();

    // Update filters to apply the cleared state
    const facetInputsComponent = this.closest('facet-inputs-component');
    if (facetInputsComponent && 'updateFilters' in facetInputsComponent && typeof facetInputsComponent.updateFilters === 'function') {
      facetInputsComponent.updateFilters();
    }
  }

  handleCheckboxChange(/** @type {Event} */ e) {
    // Prevent the parent facet inputs component from handling this change before
    // we inject the real price params (filter.v.price.gte/lte) into the form.
    // We'll call updateFilters manually after creating hidden inputs.
    e.stopPropagation();

    const target = /** @type {HTMLInputElement} */ (e.target);
    if (!target) return;

    // If checked, create hidden inputs and update filters
    if (target.checked) {
      // Keep attribute in sync so CSS/observer behavior is consistent across AJAX updates
      const checkboxes = this.refs.rangeCheckbox;
      if (checkboxes && Array.isArray(checkboxes)) {
        checkboxes.forEach((checkbox) => {
          const input = /** @type {HTMLInputElement} */ (checkbox);
          if (input === target) {
            input.setAttribute('checked', '');
          } else {
            input.removeAttribute('checked');
          }
        });
      }

      const min = target.dataset.min || '0';
      const max = target.dataset.max !== undefined ? target.dataset.max : '';

      this.#createHiddenInputs(min, max);

      // Update filters
      const facetInputsComponent = this.closest('facet-inputs-component');
      if (facetInputsComponent && 'updateFilters' in facetInputsComponent && typeof facetInputsComponent.updateFilters === 'function') {
        facetInputsComponent.updateFilters();
      }
    }
  }

  handleSliderChange(/** @type {boolean} */ shouldUpdateFilters = false) {
    const sliderMin = this.refs.sliderMin;
    const sliderMax = this.refs.sliderMax;

    if (!sliderMin || Array.isArray(sliderMin) || !sliderMax || Array.isArray(sliderMax)) return;

    const minSlider = /** @type {HTMLInputElement} */ (sliderMin);
    const maxSlider = /** @type {HTMLInputElement} */ (sliderMax);

    // Round values to prevent floating-point display glitches (e.g., 106.00000001)
    let minValue = Math.round(parseFloat(minSlider.value));
    let maxValue = Math.round(parseFloat(maxSlider.value));

    const minGap = Math.max(0, this.increment || 0);

    // Prevent the handles from crossing; honor minGap if provided (can be 0).
    if (minValue > maxValue - minGap) {
      minValue = Math.max(0, maxValue - minGap);
      minSlider.value = String(minValue);
    }

    if (maxValue < minValue + minGap) {
      maxValue = Math.min(this.rangeMax, minValue + minGap);
      maxSlider.value = String(maxValue);
    }

    // Update current values immediately to prevent sync issues
    this.currentMin = minValue;
    this.currentMax = maxValue;

    this.updateSliderTrack();

    const minInput = this.refs.minInput;
    const maxInput = this.refs.maxInput;
    if (minInput && !Array.isArray(minInput)) {
      /** @type {HTMLInputElement} */ (minInput).value = String(minValue);
    }
    if (maxInput && !Array.isArray(maxInput)) {
      /** @type {HTMLInputElement} */ (maxInput).value = String(maxValue);
    }

    // Only update filters when user releases the slider (on 'change' event)
    // Save current values before DOM update so they can be restored
    if (shouldUpdateFilters) {
      this.pendingMinValue = minValue;
      this.pendingMaxValue = maxValue;
      this.updateFilters(String(minValue), String(maxValue));
    }
  }

  handleInputChange() {
    const minInput = this.refs.minInput;
    const maxInput = this.refs.maxInput;

    let minValue = 0;
    let maxValue = this.rangeMax;

    if (minInput && !Array.isArray(minInput)) {
      // Round to prevent floating-point display glitches
      minValue = Math.round(parseFloat(/** @type {HTMLInputElement} */ (minInput).value) || 0);
    }
    if (maxInput && !Array.isArray(maxInput)) {
      // Round to prevent floating-point display glitches
      maxValue = Math.round(parseFloat(/** @type {HTMLInputElement} */ (maxInput).value) || this.rangeMax);
    }

    minValue = Math.max(0, Math.min(minValue, this.rangeMax));
    maxValue = Math.max(0, Math.min(maxValue, this.rangeMax));

    // Update current values immediately to prevent sync issues
    this.currentMin = minValue;
    this.currentMax = maxValue;

    const sliderMin = this.refs.sliderMin;
    const sliderMax = this.refs.sliderMax;
    if (sliderMin && !Array.isArray(sliderMin)) {
      /** @type {HTMLInputElement} */ (sliderMin).value = String(minValue);
    }
    if (sliderMax && !Array.isArray(sliderMax)) {
      /** @type {HTMLInputElement} */ (sliderMax).value = String(maxValue);
    }

    this.updateSliderTrack();

    // Don't auto-update filters when typing manually
    // Filters will be applied when user clicks "Apply Filters" button
  }

  updateSliderTrack() {
    const sliderTrack = this.refs.sliderTrack;
    const sliderMin = this.refs.sliderMin;
    const sliderMax = this.refs.sliderMax;

    if (!sliderTrack || Array.isArray(sliderTrack)) return;
    if (!sliderMin || Array.isArray(sliderMin) || !sliderMax || Array.isArray(sliderMax)) return;

    const track = /** @type {HTMLElement} */ (sliderTrack);
    const minSlider = /** @type {HTMLInputElement} */ (sliderMin);
    const maxSlider = /** @type {HTMLInputElement} */ (sliderMax);

    // Update rangeMax from dataset in case it changed (round to prevent floating-point issues)
    const newRangeMax = Math.round(parseFloat(this.dataset.rangeMax || '0'));
    if (newRangeMax > 0) {
      this.rangeMax = newRangeMax;
    }

    const minValue = Math.round(parseFloat(minSlider.value)) || 0;
    const maxValue = Math.round(parseFloat(maxSlider.value)) || this.rangeMax;

    const minPercent = (minValue / this.rangeMax) * 100;
    const maxPercent = (maxValue / this.rangeMax) * 100;

    const leftValue = `${minPercent}%`;
    const rightValue = `${100 - maxPercent}%`;

    // Apply position styles - this will make track visible via CSS selector
    track.style.left = leftValue;
    track.style.right = rightValue;
  }


  #removeHiddenInputs() {
    const form = this.#getFacetsForm();
    if (!form) {
      return;
    }

    // Ensure we have the param names (fallback to dataset if instance properties are empty)
    const minParamName = this.minParam || this.dataset.minParam || '';
    const maxParamName = this.maxParam || this.dataset.maxParam || '';

    if (!minParamName) {
      return;
    }

    // Only remove hidden inputs created for URL/filter submission.
    // Do NOT remove the visible min/max text inputs in the UI
    const selector = `input[type="hidden"][name="${minParamName}"]${maxParamName ? `, input[type="hidden"][name="${maxParamName}"]` : ''}`;
    form.querySelectorAll(selector).forEach((input) => {
      input.remove();
    });
  }

  #createHiddenInputs(/** @type {string} */ min, /** @type {string} */ max) {
    const form = this.#getFacetsForm();
    if (!form) {
      return;
    }

    // Ensure we have the param names (fallback to dataset if instance properties are empty)
    const minParamName = this.minParam || this.dataset.minParam || '';
    const maxParamName = this.maxParam || this.dataset.maxParam || '';

    if (!minParamName) {
      return;
    }

    this.#removeHiddenInputs();
    const minValue = parseFloat(min).toFixed(2);
    const minInput = document.createElement('input');
    minInput.type = 'hidden';
    minInput.name = minParamName;
    minInput.value = minValue;

    form.appendChild(minInput);

    if (max && max !== '' && maxParamName) {
      const maxValue = parseFloat(max).toFixed(2);

      const maxInput = document.createElement('input');
      maxInput.type = 'hidden';
      maxInput.name = maxParamName;
      maxInput.value = maxValue;

      form.appendChild(maxInput);
    }
  }

  updateFilters(/** @type {string} */ min, /** @type {string} */ max) {
    this.#createHiddenInputs(min, max);

    const facetInputsComponent = this.closest('facet-inputs-component');
    if (facetInputsComponent && 'updateFilters' in facetInputsComponent && typeof facetInputsComponent.updateFilters === 'function') {
      facetInputsComponent.updateFilters();
    }
  }

  clearFilter() {
    // Reset state
    this.pendingMinValue = null;
    this.pendingMaxValue = null;
    
    const sliderMin = this.refs.sliderMin;
    const sliderMax = this.refs.sliderMax;
    const minInput = this.refs.minInput;
    const maxInput = this.refs.maxInput;

    if (sliderMin && !Array.isArray(sliderMin)) {
      /** @type {HTMLInputElement} */ (sliderMin).value = '0';
    }
    if (sliderMax && !Array.isArray(sliderMax)) {
      /** @type {HTMLInputElement} */ (sliderMax).value = String(this.rangeMax);
    }
    if (minInput && !Array.isArray(minInput)) {
      /** @type {HTMLInputElement} */ (minInput).value = '';
    }
    if (maxInput && !Array.isArray(maxInput)) {
      /** @type {HTMLInputElement} */ (maxInput).value = '';
    }

    // Clear preset range radios (desktop)
    const checkboxes = this.refs.rangeCheckbox;
    if (checkboxes && Array.isArray(checkboxes)) {
      checkboxes.forEach((checkbox) => {
        const input = /** @type {HTMLInputElement} */ (checkbox);
        input.checked = false;
        input.removeAttribute('checked');
      });
    }

    this.updateSliderTrack();
    this.#removeHiddenInputs();
  }

}

if (!customElements.get('td-price-range-facet-component')) {
  customElements.define('td-price-range-facet-component', TdPriceRangeFacetComponent);
}
